import { createReducer, on } from '@ngrx/store';
import { AuthState, initialAuthState } from './models/auth.models';
import { AuthActions } from './auth.actions';

/**
 * Auth reducer using createReducer for immutable state updates
 * Handles all auth-related actions and state transitions
 */
export const authReducer = createReducer(
  initialAuthState,
  
  // Initial auth check
  on(AuthActions.checkInitialAuth, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, login: true },
    error: { ...state.error, login: null }
  })),
  
  on(AuthActions.initialAuthCheckSuccess, (state, { user, isAuthenticated }): AuthState => ({
    ...state,
    isAuthenticated,
    user,
    loading: { ...state.loading, login: false },
    lastProcessedUserId: user?.sub || null
  })),
  
  on(AuthActions.initialAuthCheckCompleted, (state): AuthState => ({
    ...state,
    initialAuthCheckCompleted: true,
    loading: { ...state.loading, login: false }
  })),
  
  // Login
  on(AuthActions.login, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, login: true },
    error: { ...state.error, login: null }
  })),
  
  on(AuthActions.loginSuccess, (state, { user }): AuthState => ({
    ...state,
    isAuthenticated: true,
    user,
    loading: { ...state.loading, login: false },
    error: { ...state.error, login: null },
    lastProcessedUserId: user.sub ?? null
  })),
  
  on(AuthActions.loginFailure, (state, { error }): AuthState => ({
    ...state,
    isAuthenticated: false,
    loading: { ...state.loading, login: false },
    error: { ...state.error, login: error.message }
  })),
  
  // OAuth login
  on(AuthActions.oAuthLogin, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, login: true },
    error: { ...state.error, login: null },
    processingOAuth: true
  })),
  
  on(AuthActions.oAuthLoginSuccess, (state, { user }): AuthState => ({
    ...state,
    isAuthenticated: true,
    user,
    loading: { ...state.loading, login: false },
    error: { ...state.error, login: null },
    processingOAuth: false,
    lastProcessedUserId: user.sub ?? null
  })),
  
  on(AuthActions.oAuthLoginFailure, (state, { error }): AuthState => ({
    ...state,
    isAuthenticated: false,
    loading: { ...state.loading, login: false },
    error: { ...state.error, login: error.message },
    processingOAuth: false
  })),
  
  on(AuthActions.oAuthProcessingStarted, (state): AuthState => ({
    ...state,
    processingOAuth: true,
    loading: { ...state.loading, login: true }
  })),
  
  on(AuthActions.oAuthProcessingCompleted, (state): AuthState => ({
    ...state,
    processingOAuth: false
  })),
  
  // Logout
  on(AuthActions.logout, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, logout: true },
    error: { ...state.error, logout: null }
  })),
  
  on(AuthActions.logoutSuccess, (): AuthState => ({
    ...initialAuthState,
    initialAuthCheckCompleted: true,
    // CRITICAL: Keep logout loading true until navigation completes
    // This prevents content flash before redirect to login page
    loading: { ...initialAuthState.loading, logout: true }
  })),
  
  on(AuthActions.logoutFailure, (state, { error }): AuthState => ({
    ...state,
    loading: { ...state.loading, logout: false },
    error: { ...state.error, logout: error.message }
  })),
  
  // Registration
  on(AuthActions.register, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, registration: true },
    error: { ...state.error, registration: null }
  })),
  
  on(AuthActions.registerSuccess, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, registration: false },
    error: { ...state.error, registration: null }
  })),
  
  on(AuthActions.registerFailure, (state, { error }): AuthState => ({
    ...state,
    loading: { ...state.loading, registration: false },
    error: { ...state.error, registration: error.message }
  })),
  
  // User sync with backend
  on(AuthActions.syncUserWithBackend, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, userSync: true },
    error: { ...state.error, userSync: null }
  })),
  
  on(AuthActions.syncUserWithBackendSuccess, (state, { user }): AuthState => ({
    ...state,
    user,
    loading: { ...state.loading, userSync: false },
    error: { ...state.error, userSync: null }
  })),
  
  on(AuthActions.syncUserWithBackendFailure, (state, { error }): AuthState => ({
    ...state,
    loading: { ...state.loading, userSync: false },
    error: { ...state.error, userSync: error.message }
  })),
  
  on(AuthActions.userBackendSyncCompleted, (state): AuthState => ({
    ...state,
    userBackendSyncCompleted: true
  })),
  
  // Update user data
  on(AuthActions.updateUserData, (state, { user }): AuthState => ({
    ...state,
    user
  })),
  
  on(AuthActions.updateUserAttributes, (state, { attributes }): AuthState => ({
    ...state,
    user: state.user ? { ...state.user, ...attributes } : null
  })),
  
  // Token management
  on(AuthActions.refreshToken, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, tokenRefresh: true },
    error: { ...state.error, tokenRefresh: null }
  })),
  
  on(AuthActions.refreshTokenSuccess, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, tokenRefresh: false }
  })),
  
  on(AuthActions.refreshTokenFailure, (state, { error }): AuthState => ({
    ...state,
    loading: { ...state.loading, tokenRefresh: false },
    error: { ...state.error, tokenRefresh: error.message }
  })),
  
  // Password change
  on(AuthActions.changePassword, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, passwordChange: true },
    error: { ...state.error, passwordChange: null }
  })),
  
  on(AuthActions.changePasswordSuccess, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, passwordChange: false },
    error: { ...state.error, passwordChange: null }
  })),
  
  on(AuthActions.changePasswordFailure, (state, { error }): AuthState => ({
    ...state,
    loading: { ...state.loading, passwordChange: false },
    error: { ...state.error, passwordChange: error.message }
  })),
  
  // Email confirmation
  on(AuthActions.confirmEmail, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, emailConfirmation: true },
    error: { ...state.error, emailConfirmation: null }
  })),
  
  on(AuthActions.confirmEmailSuccess, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, emailConfirmation: false },
    error: { ...state.error, emailConfirmation: null }
  })),
  
  on(AuthActions.confirmEmailFailure, (state, { error }): AuthState => ({
    ...state,
    loading: { ...state.loading, emailConfirmation: false },
    error: { ...state.error, emailConfirmation: error.message }
  })),
  
  // Resend confirmation code
  on(AuthActions.resendConfirmationCode, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, resendCode: true },
    error: { ...state.error, resendCode: null }
  })),
  
  on(AuthActions.resendConfirmationCodeSuccess, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, resendCode: false },
    error: { ...state.error, resendCode: null }
  })),
  
  on(AuthActions.resendConfirmationCodeFailure, (state, { error }): AuthState => ({
    ...state,
    loading: { ...state.loading, resendCode: false },
    error: { ...state.error, resendCode: error.message }
  })),
  
  // Password reset request
  on(AuthActions.requestPasswordReset, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, passwordReset: true },
    error: { ...state.error, passwordReset: null }
  })),
  
  on(AuthActions.requestPasswordResetSuccess, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, passwordReset: false },
    error: { ...state.error, passwordReset: null }
  })),
  
  on(AuthActions.requestPasswordResetFailure, (state, { error }): AuthState => ({
    ...state,
    loading: { ...state.loading, passwordReset: false },
    error: { ...state.error, passwordReset: error.message }
  })),
  
  // Confirm password reset
  on(AuthActions.confirmPasswordReset, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, confirmPasswordReset: true },
    error: { ...state.error, confirmPasswordReset: null }
  })),
  
  on(AuthActions.confirmPasswordResetSuccess, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, confirmPasswordReset: false },
    error: { ...state.error, confirmPasswordReset: null }
  })),
  
  on(AuthActions.confirmPasswordResetFailure, (state, { error }): AuthState => ({
    ...state,
    loading: { ...state.loading, confirmPasswordReset: false },
    error: { ...state.error, confirmPasswordReset: error.message }
  })),
  
  // Amplify Hub events
  on(AuthActions.hubSignedIn, AuthActions.hubAutoSignIn, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, login: true }
  })),
  
  on(AuthActions.hubSignedOut, (): AuthState => ({
    ...initialAuthState,
    initialAuthCheckCompleted: true
  })),
  
  on(AuthActions.hubSignInFailure, (state, { error }): AuthState => ({
    ...state,
    loading: { ...state.loading, login: false },
    error: { ...state.error, login: error.message }
  })),
  
  // Authentication state
  on(AuthActions.setAuthenticated, (state, { isAuthenticated }): AuthState => ({
    ...state,
    isAuthenticated
  })),
  
  on(AuthActions.setLastProcessedUser, (state, { userId }): AuthState => ({
    ...state,
    lastProcessedUserId: userId
  })),
  
  // Error handling
  on(AuthActions.clearLoginError, (state): AuthState => ({
    ...state,
    error: { ...state.error, login: null }
  })),
  
  on(AuthActions.clearRegistrationError, (state): AuthState => ({
    ...state,
    error: { ...state.error, registration: null }
  })),
  
  on(AuthActions.clearPasswordChangeError, (state): AuthState => ({
    ...state,
    error: { ...state.error, passwordChange: null }
  })),
  
  on(AuthActions.clearEmailConfirmationError, (state): AuthState => ({
    ...state,
    error: { ...state.error, emailConfirmation: null }
  })),
  
  on(AuthActions.clearPasswordResetError, (state): AuthState => ({
    ...state,
    error: { ...state.error, passwordReset: null, confirmPasswordReset: null }
  })),
  
  on(AuthActions.clearOAuthLoadingState, (state): AuthState => ({
    ...state,
    loading: { ...state.loading, login: false },
    processingOAuth: false
  })),
  
  on(AuthActions.clearAllErrors, (state): AuthState => ({
    ...state,
    error: {
      login: null,
      logout: null,
      registration: null,
      userSync: null,
      tokenRefresh: null,
      passwordChange: null,
      emailConfirmation: null,
      resendCode: null,
      passwordReset: null,
      confirmPasswordReset: null
    }
  })),
  
  // Reset state
  on(AuthActions.resetAuthState, (): AuthState => initialAuthState)
);
